
import type { ApiResponse } from "../../utils/apiClient";
import { adminSilentApi, userApi, userSilentApi } from "../../utils/apiInstances";

export type AuthContext = "admin" | "user";
type QueryValue = string | number | boolean | null | undefined;
type Payload = Record<string, unknown>;

/**
 * Unified function to fetch transactions.
 */
export const handleGetTransactions = async <TData = unknown>(
    context: AuthContext,
    params: Record<string, QueryValue> = {},
    customEndpoint?: string
): Promise<ApiResponse<TData>> => {
    try {
        const searchParams = new URLSearchParams();
        const queryParams = {
            page: params.page ?? 1,
            per_page: params.per_page ?? 10,
            ...params,
        };

        Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                searchParams.append(key, String(value));
            }
        });

        const queryString = searchParams.toString();
        const api = context === "admin" ? adminSilentApi : userSilentApi;
        const defaultEndpoint = context === "admin" ? "/admin/admin-transactions" : "/user/transactions";
        const endpoint = customEndpoint || defaultEndpoint;
        const uri = `${endpoint}${queryString ? `?${queryString}` : ""}`;

        const response = await api<TData>("GET", uri);
        return response;
    } catch (error) {
        console.error(`Error fetching transactions (${context}):`, error);
        throw error;
    }
};

/**
 * Admin-only: Reconfirm a transaction.
 */
export const handleAdminReconfirmTransaction = async (
    transactionId: string | number,
    payload: Payload = {}
) => {
    try {
        const response = await adminSilentApi(
            "POST",
            `/admin/transactions/${encodeURIComponent(transactionId)}/reconfirm`,
            payload
        );
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * User-only: Verify a transaction.
 */
export const handleVerifyTransaction = async (userData: Payload) => {
    try {
        const response = await userApi("POST", "/user/transactions", userData);
        return response;
    } catch (error) {
        console.error("handleVerifyTransaction error:", error);
        throw error;
    }
};

/**
 * User-only: Subscribe to a plan.
 */
export const handlePlanSubscribe = async (
    userData: Payload,
    onSuccess?: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
) => {
    try {
        const response = await userApi("POST", "/user/subscriptions", userData);
        onSuccess?.(response);
        return response;
    } catch (error) {
        console.error("handlePlanSubscribe error:", error);
        onError?.(error);
        throw error;
    }
};

/**
 * User-only: Pay with card.
 */
export const handlePayWithCard = async (
    userData: Payload,
    onSuccess?: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
) => {
    try {
        const response = await userApi("POST", "/user/paywithcard", userData);
        onSuccess?.(response);
        return response;
    } catch (error) {
        console.error("handlePayWithCard error:", error);
        onError?.(error);
        throw error;
    }
};

/**
 * User-only: Top up wallet.
 */
export const handleTopUpWallet = async (
    userData: Payload,
    onSuccess?: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
) => {
    try {
        const response = await userApi("POST", "/user/fund-wallet", userData);
        onSuccess?.(response);
        return response;
    } catch (error) {
        console.error("handleTopUpWallet error:", error);
        onError?.(error);
        throw error;
    }
};
